import React, { useState, useEffect } from 'react';
import { ButtonGroup, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Box, Typography, Autocomplete, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTranslation } from 'react-i18next';
import api from '../utils/apiClient';
import { getErrorMessage } from '../utils/errorMessage';
import DeviceStatusTimeline from './DeviceStatusTimeline';
import { useNotifications } from '../context/NotificationContext';
import { API_BASE } from '../utils/apiConfig';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const AdminControlDevice = ({ selectedRows, deviceIds, uptimeData, devices, onBack, onDataRefresh, onOpenMoveDialog, onTimelineOpenChange, onClearSelection }) => {
  const theme = useTheme();
  const { t } = useTranslation('admin');
  const { showSuccess, showError, showInfo } = useNotifications();
  const [openDialog, setOpenDialog] = useState(false);
  const [actionType, setActionType] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [openStatusTimeline, setOpenStatusTimeline] = useState(false);

  // State для формы добавления устройства
  const [newDevice, setNewDevice] = useState({
    ip_address: '',
    model: '',
    hosting_id: '',
    agent_id: '',
    tdevtype: 'DAntminer',
    location: '',
    tags: [],
    owner_id: ''
  });

  // Справочники для автодополнения
  const [hostings, setHostings] = useState([]);
  const [agents, setAgents] = useState([]);
  const [owners, setOwners] = useState([]);
  const [locations, setLocations] = useState([]);
  const [tagsOptions, setTagsOptions] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);
  
  // Состояние для смены владельца
  const [newOwnerId, setNewOwnerId] = useState('');
  
  // Состояние для массовой настройки хешрейта
  const [hashrateSettings, setHashrateSettings] = useState({
    algorithm: '',
    hashrate_unit: ''
  });

  // Состояние для применения модели
  const [models, setModels] = useState([]);
  const [selectedModelId, setSelectedModelId] = useState('');

  // Уведомляем родительский компонент об открытии/закрытии Timeline
  useEffect(() => {
    if (onTimelineOpenChange) {
      onTimelineOpenChange(openStatusTimeline);
    }
  }, [openStatusTimeline, onTimelineOpenChange]);

  const handleAction = async (action) => {
    // Если это действие "move" и передан callback для открытия диалога перемещения
    if (action === 'move' && onOpenMoveDialog) {
      onOpenMoveDialog();
      return;
    }
    
    setActionType(action);
    setOpenDialog(true);
    
    // Загружаем справочники при открытии диалога добавления устройства или смены владельца
    if (action === 'add_device' || action === 'change_owner') {
      await loadAllLists();
    }

    // Загружаем список моделей при открытии диалога применения модели
    if (action === 'apply_model') {
      await loadModels();
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setActionType('');
    resetDeviceForm();
    setNewOwnerId('');
    setHashrateSettings({
      algorithm: '',
      hashrate_unit: ''
    });
    setSelectedModelId('');
  };

  const resetDeviceForm = () => {
    setNewDevice({
      ip_address: '',
      model: '',
      hosting_id: '',
      agent_id: '',
      tdevtype: 'DAntminer',
      location: '',
      tags: [],
      owner_id: ''
    });
  };

  const handleDeviceFieldChange = (field, value) => {
    setNewDevice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Загрузка всех справочников
  const loadAllLists = async () => {
    setLoadingLists(true);
    try {
      const [hostingsRes, agentsRes, ownersRes, locationsRes, tagsRes] = await Promise.all([
        api.get(`${API_BASE}data/hosting`),
        api.get(`${API_BASE}data/agent`),
        api.get(`${API_BASE}data/owner`),
        api.get(`${API_BASE}data/location`),
        api.get(`${API_BASE}data/tag`)
      ]);

      const loadedHostings = hostingsRes.data?.results || hostingsRes.data || [];
      const loadedAgents = agentsRes.data?.results || agentsRes.data || [];
      const loadedOwners = ownersRes.data?.results || ownersRes.data || [];
      const loadedLocations = locationsRes.data?.results || locationsRes.data || [];
      const loadedTags = tagsRes.data?.results || tagsRes.data || [];
      
      setHostings(loadedHostings);
      setAgents(loadedAgents);
      setOwners(loadedOwners);
      setLocations(loadedLocations);
      setTagsOptions(loadedTags);
    } catch (error) {
      alert(t('adminControlErrorLoadLists', { error: error.message }));
    } finally {
      setLoadingLists(false);
    }
  };

  // Загрузка списка моделей
  const loadModels = async () => {
    setLoadingLists(true);
    try {
      const response = await api.get(`${API_BASE}data/models`);
      const loadedModels = response.data?.results || response.data || [];
      setModels(loadedModels);
    } catch (error) {
      showError(t('adminControlErrorLoadModels', { error: error.message }));
    } finally {
      setLoadingLists(false);
    }
  };

  // Получить агентов для выбранного хостинга
  const getAgentsForHosting = (hostingId) => {
    if (!hostingId) return [];
    // Если у агентов есть привязка к хостингу, фильтруем
    const filtered = agents.filter(agent => agent.hosting?.id === hostingId || agent.hosting_id === hostingId);
    // Если фильтрация вернула пустой массив, но агенты есть, показываем всех
    const result = filtered.length > 0 ? filtered : agents;
    return result;
  };

  const handleExecuteAction = async () => {
    setIsLoading(true);

    try {
      if (actionType === 'delete') {
        const devices = deviceIds || selectedRows;
        if (!devices || devices.length === 0) {
          throw new Error(t('adminControlErrorNoDevicesDelete'));
        }

        // Формируем payload в зависимости от количества устройств
        const payload = devices.length === 1
          ? { device_id: devices[0] }
          : { device_ids: devices };

        const response = await api.post(
          `${API_BASE_URL}agent/device_delete`,
          payload
        );

        if (response.data.success) {
          const msg = response.data.message || '';
            const cnt = response.data.devices_count || devices.length;
            alert(t('adminControlDevicesMarkedForDeletion', { message: msg, count: cnt }));

          // Снимаем выделение после успешного удаления
          if (onClearSelection) {
            onClearSelection();
          }
        } else {
          throw new Error(response.data.message || t('adminControlErrorUnknownDelete'));
        }

        handleDialogClose();

        // Обновляем страницу или вызываем коллбэк для обновления списка
        if (onBack) {
          onBack();
        }
      } else if (actionType === 'add_device') {
        // Валидация обязательных полей
        if (!newDevice.ip_address || !newDevice.model || !newDevice.hosting_id || !newDevice.agent_id || !newDevice.tdevtype) {
          throw new Error(t('adminControlErrorAllFieldsRequired'));
        }

        // Формируем payload
        const payload = {
          ip_address: newDevice.ip_address.trim(),
          model: newDevice.model.trim(),
          hosting_id: newDevice.hosting_id,
          agent_id: newDevice.agent_id,
          tdevtype: newDevice.tdevtype
        };

        // Добавляем необязательные поля если они заполнены
        if (newDevice.location) {
          payload.location = newDevice.location;
        }
        if (newDevice.owner_id) {
          payload.owner_id = newDevice.owner_id;
        }
        if (Array.isArray(newDevice.tags) && newDevice.tags.length > 0) {
          // Преобразуем массив тегов в объект { tag: true }
          const tagsObj = {};
          newDevice.tags.forEach(tag => {
            if (tag && tag.trim()) {
              tagsObj[tag.trim()] = true;
            }
          });
          if (Object.keys(tagsObj).length > 0) {
            payload.tags = tagsObj;
          }
        }

        await api.post(
          `${API_BASE_URL}device/create`,
          payload
        );

        alert(t('adminControlDeviceCreated'));

        // Снимаем выделение после успешного создания (хотя обычно при создании ничего не выбрано)
        if (onClearSelection) {
          onClearSelection();
        }

        handleDialogClose();

        // Обновляем страницу или вызываем коллбэк для обновления списка
        if (onBack) {
          onBack();
        }
      } else if (actionType === 'change_owner') {
        const devices = deviceIds || selectedRows;
        if (!devices || devices.length === 0) {
          throw new Error(t('adminControlErrorNoDevicesChangeOwner'));
        }

        if (!newOwnerId) {
          throw new Error(t('adminControlErrorNoOwnerSelected'));
        }

        // Формируем payload для bulk_update
        const payload = {
          devices: devices.map(deviceId => ({
            device_id: deviceId,
            owner_id: newOwnerId
          }))
        };

        const response = await api.post(
          `${API_BASE}data_device/bulk_update`,
          payload
        );

        const responseData = response.data;

        // Обрабатываем ответ API
        if (responseData.success === true) {
          const successCount = responseData.success_count || 0;
          const errorCount = responseData.error_count || 0;

          // Сбрасываем локальный кеш dashboard_devices_cache
          localStorage.removeItem('dashboard_devices_cache');

          let message = responseData.message || t('adminControlProcessedDevices', { count: devices.length });
          if (errorCount > 0) {
            // Если есть ошибки, показываем детали
            const errorDevices = responseData.results?.filter(r => r.success === false) || [];
            const errorMessages = errorDevices.map(e => e.message || t('adminControlErrorFallback')).join('; ');
            showError(t('adminControlErrorsDetail', { message, errors: errorMessages }));
            // При частичной ошибке не снимаем выделение
          } else {
            showSuccess(message);
            // Снимаем выделение только при полном успехе
            if (onClearSelection) {
              onClearSelection();
            }
          }
        } else {
          throw new Error(responseData.message || t('adminControlErrorChangeOwner'));
        }

        handleDialogClose();

        // Обновляем страницу или вызываем коллбэк для обновления списка
        if (onDataRefresh) {
          onDataRefresh();
        } else if (onBack) {
          onBack();
        }
      } else if (actionType === 'hashrate_update') {
        const devices = deviceIds || selectedRows;
        if (!devices || devices.length === 0) {
          throw new Error(t('adminControlErrorNoDevicesHashrate'));
        }

        if (!hashrateSettings.algorithm || !hashrateSettings.hashrate_unit) {
          throw new Error(t('adminControlErrorHashrateFields'));
        }

        // Формируем payload для bulk_update
        const payload = {
          devices: devices.map(deviceId => ({
            device_id: deviceId,
            algorithm: hashrateSettings.algorithm,
            hashrate_unit: hashrateSettings.hashrate_unit
          }))
        };

        const response = await api.post(
          `${API_BASE}data_device/bulk_update`,
          payload
        );

        const responseData = response.data;

        // Обрабатываем ответ API
        if (responseData.success === true) {
          const successCount = responseData.success_count || 0;
          const errorCount = responseData.error_count || 0;

          // Сбрасываем локальный кеш dashboard_devices_cache
          localStorage.removeItem('dashboard_devices_cache');

          let message = responseData.message || t('adminControlProcessedDevices', { count: devices.length });
          if (errorCount > 0) {
            // Если есть ошибки, показываем детали
            const errorDevices = responseData.results?.filter(r => r.success === false) || [];
            const errorMessages = errorDevices.map(e => e.message || t('adminControlErrorFallback')).join('; ');
            showError(t('adminControlErrorsDetail', { message, errors: errorMessages }));
            // При частичной ошибке не снимаем выделение
          } else {
            showSuccess(message);
            // Снимаем выделение только при полном успехе
            if (onClearSelection) {
              onClearSelection();
            }
          }
        } else {
          throw new Error(responseData.message || t('adminControlErrorHashrate'));
        }

        handleDialogClose();

        // Обновляем страницу или вызываем коллбэк для обновления списка
        if (onDataRefresh) {
          onDataRefresh();
        } else if (onBack) {
          onBack();
        }
      } else if (actionType === 'apply_model') {
        const devices = deviceIds || selectedRows;
        if (!devices || devices.length === 0) {
          throw new Error(t('adminControlErrorNoDevicesModel'));
        }

        if (selectedModelId === '' || selectedModelId === undefined) {
          throw new Error(t('adminControlErrorNoModelSelected'));
        }

        // Формируем payload для apply-model-settings
        const payload = {
          model_id: selectedModelId, // может быть null для сброса модели
          device_ids: devices
        };

        const response = await api.post(
          `${API_BASE}data/apply-model-settings`,
          payload
        );

        const responseData = response.data;

        // Обрабатываем ответ API
        if (responseData.success === true) {
          const successCount = responseData.success_count || 0;
          const errorCount = responseData.error_count || 0;

          // Сбрасываем локальный кеш dashboard_devices_cache
          localStorage.removeItem('dashboard_devices_cache');

          const isReset = selectedModelId === null;
          let message = responseData.message || (isReset
            ? t('adminControlModelReset', { count: successCount })
            : t('adminControlModelApplied', { count: successCount }));

          if (errorCount > 0) {
            const errorMessages = responseData.errors?.join('; ') || t('adminControlErrorApplyModelPartial');
            showError(t('adminControlErrorsDetail', { message, errors: errorMessages }));
          } else {
            showSuccess(message);
            if (onClearSelection) {
              onClearSelection();
            }
          }
        } else {
          throw new Error(responseData.message || t('adminControlErrorApplyModel'));
        }

        handleDialogClose();

        if (onDataRefresh) {
          onDataRefresh();
        } else if (onBack) {
          onBack();
        }
      } else {
        // TODO: Реализовать логику для других действий (move)
        alert(`${actionType}`);
        handleDialogClose();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderDialogContent = () => {
    const availableAgents = getAgentsForHosting(newDevice.hosting_id);

    switch (actionType) {
      case 'add_device':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t('adminControlRequiredFields')}
            </Typography>
            
            <TextField
              label={t('adminControlFieldIp')}
              value={newDevice.ip_address}
              onChange={(e) => handleDeviceFieldChange('ip_address', e.target.value)}
              placeholder={t('adminControlFieldIpPlaceholder')}
              fullWidth
              size="small"
              required
            />

            <TextField
              label={t('adminControlFieldModel')}
              value={newDevice.model}
              onChange={(e) => handleDeviceFieldChange('model', e.target.value)}
              placeholder={t('adminControlFieldModelPlaceholder')}
              fullWidth
              size="small"
              required
            />

            <FormControl fullWidth size="small" required>
              <InputLabel>{t('adminControlFieldHosting')}</InputLabel>
              <Select
                value={newDevice.hosting_id}
                onChange={(e) => {
                  handleDeviceFieldChange('hosting_id', e.target.value);
                  handleDeviceFieldChange('agent_id', ''); // Сбрасываем агента при смене хостинга
                }}
                label={t('adminControlFieldHosting')}
                disabled={loadingLists}
              >
                {loadingLists ? (
                  <MenuItem disabled>{t('adminControlLoadingMenuItem')}</MenuItem>
                ) : hostings.length === 0 ? (
                  <MenuItem disabled>{t('adminControlNoHostings')}</MenuItem>
                ) : (
                  hostings.map((hosting) => (
                    <MenuItem key={hosting.id} value={hosting.id}>
                      {hosting.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" required disabled={!newDevice.hosting_id || availableAgents.length === 0}>
              <InputLabel>{t('adminControlFieldAgent')}</InputLabel>
              <Select
                value={newDevice.agent_id}
                onChange={(e) => handleDeviceFieldChange('agent_id', e.target.value)}
                label={t('adminControlFieldAgent')}
                displayEmpty
              >
                {availableAgents.length === 0 ? (
                  <MenuItem disabled>
                    {newDevice.hosting_id ? t('adminControlNoAgents') : t('adminControlSelectHostingFirst')}
                  </MenuItem>
                ) : (
                  availableAgents.map((agent) => {
                    const suffix = agent.id ? agent.id.slice(-6) : '';
                    return (
                      <MenuItem key={agent.id} value={agent.id}>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                          <Typography variant="body2">{agent.name || t('agentButton')}</Typography>
                          {suffix && (
                            <Typography variant="caption" color="text.secondary">
                              ({suffix})
                            </Typography>
                          )}
                        </Box>
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small" required>
              <InputLabel>{t('adminControlFieldDeviceType')}</InputLabel>
              <Select
                value={newDevice.tdevtype}
                onChange={(e) => handleDeviceFieldChange('tdevtype', e.target.value)}
                label={t('adminControlFieldDeviceType')}
              >
                <MenuItem value="DAntminer">DAntminer</MenuItem>
                <MenuItem value="DWhatsMiner">DWhatsMiner</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={locations}
              getOptionLabel={(option) => option?.name || ''}
              value={locations.find(l => l?.id === newDevice.location) || null}
              onChange={(_, value) => handleDeviceFieldChange('location', value?.id || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" label={t('adminControlFieldLocation')} placeholder={t('adminControlFieldLocationPlaceholder')} />
              )}
              disabled={loadingLists}
            />

            <Autocomplete
              options={owners}
              getOptionLabel={(option) => option?.name || ''}
              value={owners.find(o => o?.id === newDevice.owner_id) || null}
              onChange={(_, value) => handleDeviceFieldChange('owner_id', value?.id || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" label={t('adminControlFieldOwner')} placeholder={t('adminControlFieldOwnerPlaceholder')} />
              )}
              disabled={loadingLists}
            />

            <Autocomplete
              multiple
              freeSolo
              options={tagsOptions}
              value={Array.isArray(newDevice.tags) ? newDevice.tags : []}
              onChange={(_, value) => handleDeviceFieldChange('tags', value)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} key={index} />
                ))
              }
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  size="small" 
                  label={t('adminControlFieldTags')}
                  placeholder={t('adminControlFieldTagsPlaceholder')} 
                />
              )}
              disabled={loadingLists}
            />
          </Box>
        );
      
      case 'delete':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6" color="error">{t('adminControlDeleteTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adminControlDeleteConfirm')}
            </Typography>
            <Typography variant="body2" color="error" fontWeight="bold">
              {t('adminControlSelectedDevices', { count: deviceIds?.length || selectedRows.length })}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('adminControlDeleteIrreversible')}
            </Typography>
          </Box>
        );
      
      case 'change_owner':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('adminControlChangeOwnerTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adminControlSelectedDevices', { count: deviceIds?.length || selectedRows.length })}
            </Typography>
            <Autocomplete
              options={owners}
              getOptionLabel={(o) => {
                if (!o) return '';
                return o?.email ? `${o.name} (${o.email})` : (o?.name || '');
              }}
              value={owners.find(o => o?.id === newOwnerId) || null}
              onChange={(_, val) => setNewOwnerId(val?.id || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" label={t('adminControlSelectNewOwner')} fullWidth />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">{option?.name || ''}</Typography>
                    {option?.email && (
                      <Typography variant="caption" color="text.secondary">
                        {option.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              disabled={loadingLists}
            />
          </Box>
        );
      
      case 'hashrate_update':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('adminControlHashrateTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adminControlSelectedDevices', { count: deviceIds?.length || selectedRows.length })}
            </Typography>

            {/* Алгоритм */}
            <Autocomplete
              options={[
                { id: 'SHA-256', name: 'SHA-256' },
                { id: 'Scrypt', name: 'Scrypt' },
                { id: 'X11', name: 'X11' },
                { id: 'Sia', name: 'Sia' },
                { id: 'Kaspa', name: 'Kaspa' },
                { id: 'Equihash', name: 'Equihash' }
              ]}
              getOptionLabel={(o) => o?.name || ''}
              value={{ id: hashrateSettings.algorithm || '', name: hashrateSettings.algorithm || '' }}
              onChange={(_, val) => {
                const algorithm = val?.id || '';
                // Автоматически выбираем единицу измерения на основе алгоритма
                let defaultUnit = 'TH/s';
                if (algorithm === 'Scrypt') {
                  defaultUnit = 'MH/s';
                } else if (algorithm === 'Equihash') {
                  defaultUnit = 'TH/s'; // Для Equihash используем TH/s, но показываем в kSol/s
                }
                setHashrateSettings({
                  algorithm: algorithm,
                  hashrate_unit: defaultUnit
                });
              }}
              renderInput={(params) => <TextField {...params} size="small" label={t('adminControlAlgorithm')} fullWidth />}
            />

            {/* hashrate unit */}
            <Autocomplete
              options={[
                'H/s','kH/s','MH/s','GH/s','TH/s','PH/s','EH/s'
              ]}
              getOptionLabel={(o) => o || ''}
              value={hashrateSettings.hashrate_unit || ''}
              onChange={(_, val) => setHashrateSettings(prev => ({ ...prev, hashrate_unit: val || '' }))}
              renderInput={(params) => <TextField {...params} size="small" label={t('adminControlHashrateUnit')} fullWidth />}
            />
          </Box>
        );

      case 'apply_model':
        const modelsWithReset = [
          { id: null, model: t('adminControlResetModel'), algorithm: '', hashrate_unit: '', isReset: true },
          ...models
        ];

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">{t('adminControlApplyModelTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('adminControlSelectedDevices', { count: deviceIds?.length || selectedRows.length })}
            </Typography>
            <Autocomplete
              options={modelsWithReset}
              getOptionLabel={(o) => {
                if (!o) return '';
                if (o.isReset) return o.model;
                return `${o.model} (${o.algorithm}, ${o.hashrate_unit})`;
              }}
              value={modelsWithReset.find(m => m?.id === selectedModelId) || null}
              onChange={(_, val) => setSelectedModelId(val?.id === null ? null : (val?.id || ''))}
              renderInput={(params) => (
                <TextField {...params} size="small" label={t('adminControlSelectModel')} fullWidth />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography
                      variant="body2"
                      color={option?.isReset ? 'error' : 'inherit'}
                      fontWeight={option?.isReset ? 'bold' : 'normal'}
                    >
                      {option?.model || ''}
                    </Typography>
                    {!option?.isReset && (
                      <Typography variant="caption" color="text.secondary">
                        {option?.algorithm} • {option?.hashrate_unit}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              disabled={loadingLists}
            />
            {selectedModelId === null && (
              <Box sx={{ p: 1.5, bgcolor: 'error.main', color: 'error.contrastText', borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  {t('adminControlResetWarning')}
                </Typography>
              </Box>
            )}
            {selectedModelId && selectedModelId !== null && models.find(m => m?.id === selectedModelId) && (
              <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('adminControlSettingsApplied')}
                </Typography>
                <Typography variant="body2">
                  {t('adminControlAlgorithmLabel')}<strong>{models.find(m => m?.id === selectedModelId)?.algorithm}</strong>
                </Typography>
                <Typography variant="body2">
                  {t('adminControlHashrateUnitLabel')}<strong>{models.find(m => m?.id === selectedModelId)?.hashrate_unit}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 'auto',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1300,
          width: 'fit-content',
          height: 'fit-content',
          padding: '10px',
        }}
      >
        <ButtonGroup 
          variant="contained"
        >
          <Button 
            onClick={onBack}
            disabled={isLoading}
            startIcon={<ArrowBackIcon />}
            style={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.mode === 'dark' ? '#000' : '#fff',
            }}
          >
            {t('adminControlBack')}
          </Button>
          <Button
            onClick={() => handleAction('add_device')}
            disabled={isLoading}
            startIcon={<AddIcon />}
            style={{
              backgroundColor: '#4caf50',
              color: '#fff',
            }}
          >
            {t('adminControlAddDevice')}
          </Button>
          <Button
            onClick={() => handleAction('move')}
            disabled={isLoading}
            style={{
              backgroundColor: '#ff9800',
              color: '#fff',
            }}
          >
            {t('adminControlMove')}
          </Button>
          <Button
            onClick={() => handleAction('delete')}
            disabled={isLoading}
            style={{
              backgroundColor: '#f44336',
              color: '#fff',
            }}
          >
            {t('adminControlDeleteDevice')}
          </Button>
          <Button
            onClick={() => handleAction('change_owner')}
            disabled={isLoading}
            style={{
              backgroundColor: '#ff9800',
              color: '#fff',
            }}
          >
            {t('adminControlChangeOwner')}
          </Button>
          <Button
            onClick={() => handleAction('hashrate_update')}
            disabled={isLoading}
            style={{
              backgroundColor: '#9c27b0',
              color: '#fff',
            }}
          >
            {t('adminControlHashrate')}
          </Button>
          <Button
            onClick={() => handleAction('apply_model')}
            disabled={isLoading}
            style={{
              backgroundColor: '#00bcd4',
              color: '#fff',
            }}
          >
            {t('adminControlModel')}
          </Button>
          <Button
            onClick={() => setOpenStatusTimeline(true)}
            disabled={isLoading}
            startIcon={<TimelineIcon />}
            style={{
              backgroundColor: '#2196f3',
              color: '#fff',
            }}
          >
            {t('adminControlStatuses')}
          </Button>
        </ButtonGroup>
      </div>

      {/* Диалог для дополнительных параметров */}
      <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {actionType === 'add_device' && t('adminControlDialogAddTitle')}
          {actionType === 'delete' && t('adminControlDialogDeleteTitle')}
          {actionType === 'change_owner' && t('adminControlDialogChangeOwnerTitle')}
          {actionType === 'hashrate_update' && t('adminControlDialogHashrateTitle')}
          {actionType === 'apply_model' && t('adminControlDialogApplyModelTitle')}
        </DialogTitle>
        <DialogContent>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t('usersCancelButton')}</Button>
          <Button 
            onClick={handleExecuteAction} 
            variant="contained"
            disabled={isLoading}
            color={actionType === 'delete' ? 'error' : actionType === 'add_device' ? 'success' : 'primary'}
          >
            {isLoading ? t('adminControlExecuting') : actionType === 'add_device' ? t('adminControlCreateButton') : t('adminControlExecuteButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Компонент визуализации статусов */}
      <DeviceStatusTimeline
        open={openStatusTimeline}
        onClose={() => setOpenStatusTimeline(false)}
        devices={devices || []}
        selectedDeviceIds={deviceIds || selectedRows || []}
        uptimeData={uptimeData || []}
        showSuccess={showSuccess}
        showError={showError}
        showInfo={showInfo}
        onDataRefresh={onDataRefresh}
      />
    </>
  );
};

export default AdminControlDevice;

